import React from 'react';
import { Text } from '@smarterservices/smarter-ui';

/**
 * Simple replacements type for text interpolation
 */
type Replacements = Record<string, string | number | boolean | null | undefined>;

/**
 * Formats text content by processing:
 * 1. String replacements (e.g., {variable} -> value)
 * 2. Slack-style links (<http://example.com|Label> -> <a>Label</a>)
 * 3. Bold text (**bold** -> <strong>bold</strong>)
 *
 * @param text - The text to format
 * @param replacements - Optional object with replacement values
 * @returns Formatted React nodes
 */
export function formatText(text: string, replacements: Replacements = {}): React.ReactNode {
  if (!text) return text;

  // Check if we have any React components in the replacements
  const hasReactComponents = Object.values(replacements).some(
    value => React.isValidElement(value)
  );


  // If no React components, use the simpler string replacement approach
  if (!hasReactComponents) {
    let processedText = text;
    for (const [key, value] of Object.entries(replacements)) {
      if (value != null) {
        processedText = processedText.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(value)
        );
      }
    }
    // Process formatting after replacements to ensure **{variable}** works
    return processFormattedText(processedText);
  }

  // Handle React components by first doing string replacements, then processing formatting
  let processedText = text;
  const reactComponents: { [key: string]: React.ReactElement } = {};
  const placeholderMap: { [key: string]: string } = {};
  
  // First pass: replace React components with temporary placeholders and do string replacements
  for (const [key, value] of Object.entries(replacements)) {
    if (value != null) {
      if (React.isValidElement(value)) {
        // Store React component and create a unique placeholder
        const placeholder = `__REACT_COMPONENT_${key}_${Math.random().toString(36).substr(2, 9)}__`;
        reactComponents[placeholder] = value;
        placeholderMap[key] = placeholder;
        processedText = processedText.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          placeholder
        );
      } else {
        // Do string replacement
        processedText = processedText.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(value)
        );
      }
    }
  }

  // Process formatting on the text with string replacements done
  const formattedNodes = processFormattedText(processedText);
  
  // If no React components, we're done
  if (Object.keys(reactComponents).length === 0) {
    return formattedNodes;
  }

  // Third pass: replace React component placeholders with actual components
  const replaceReactComponents = (node: React.ReactNode): React.ReactNode => {
    if (typeof node === 'string') {
      // Check if this string contains any React component placeholders
      const placeholders = Object.keys(reactComponents);
      for (const placeholder of placeholders) {
        if (node.includes(placeholder)) {
          const parts = node.split(placeholder);
          const result: React.ReactNode[] = [];
          for (let i = 0; i < parts.length; i++) {
            if (parts[i]) result.push(parts[i]);
            if (i < parts.length - 1) {
              result.push(
                <span key={`react-${placeholder}-${i}`}>
                  {reactComponents[placeholder]}
                </span>
              );
            }
          }
          return result;
        }
      }
      return node;
    } else if (React.isValidElement(node)) {
      // Recursively process children
      const children = React.Children.map(node.props.children, replaceReactComponents);
      return React.cloneElement(node, {}, children);
    } else if (Array.isArray(node)) {
      return node.map(replaceReactComponents);
    }
    return node;
  };

  return replaceReactComponents(formattedNodes);
};

/**
 * Process text with links and formatting
 */
const processFormattedText = (text: string): React.ReactNode => {
  // Process links
  const processedWithLinks = processLinks(text);

  // Process bold formatting in each text segment
  return processBoldFormatting(processedWithLinks);
};

/**
 * Process Slack-style links in text
 *
 * @param {string} text - The text to process
 * @returns {Array<React.ReactNode>} Array of text and link elements
 */
const processLinks = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Regex to match Slack-style links: <http://example.com|Label>
  const slackLinkRegex = /<((https?:\/\/|www\.)[^|>]+)\|([^>]+)>/g;
  let match;

  while ((match = slackLinkRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const [fullMatch, url, , label] = match;

    // Validate URL before using it
    const href = validateAndFormatUrl(url);

    // Add the link
    parts.push(
      <a
        key={`link-${match.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#0374B5', textDecoration: 'underline' }}
      >
        {label || url}
      </a>
    );

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};

/**
 * Process bold formatting (text wrapped in double asterisks) in text nodes
 *
 * @param {Array<React.ReactNode>} nodes - Array of text and link elements
 * @returns {Array<React.ReactNode>} Processed nodes with bold formatting applied
 */
const processBoldFormatting = (nodes: React.ReactNode[]): React.ReactNode[] => {
  if (!nodes || nodes.length === 0) return [];

  return nodes.flatMap((node, index) => {
    // Only process string nodes
    if (typeof node !== 'string') {
      return node;
    }

    const text = node as string;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Regex to match text wrapped in double asterisks: **bold text**
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const [fullMatch, boldText] = match;

      // Add the bold text
      parts.push(
        <Text key={`bold-${index}-${match.index}`} weight="bold">
          {boldText}
        </Text>
      );

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  });
};

/**
 * Validates a URL and returns a safe URL string
 *
 * @param {string} urlString - The URL to validate
 * @returns {string} A safe URL string or '#' if invalid
 */
export const validateAndFormatUrl = (urlString: string): string => {
  try {
    // For www. URLs, prepend https:// for URL constructor
    const urlToValidate = urlString.startsWith('www.') ? `https://${urlString}` : urlString;
    new URL(urlToValidate);

    // Additional safety checks
    if ([
      'http:',
      'https:'
    ].includes(new URL(urlToValidate).protocol)) {
      // Return the properly formatted URL
      if (urlString.startsWith('www.')) {
        return `https://${urlString}`;
      }
      return urlString;
    }
    return '#'; // Invalid protocol
  } catch (e) {
    return '#'; // Invalid URL
  }
};
